if (!customElements.get('onda-video-product-carousel')) {
  customElements.define(
    'onda-video-product-carousel',
    class OndaVideoProductCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-vpc-track]');
        if (!this.track) return;

        this.previousButton = this.querySelector('[data-vpc-previous]');
        this.nextButton = this.querySelector('[data-vpc-next]');
        this.arrows = this.querySelector('.onda-video-products__arrows');
        this.videos = Array.from(this.querySelectorAll('.onda-video-products__video'));
        this.autoplayEnabled = this.dataset.autoplay === 'true';
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.onPreviousClick = () => this.scrollByCard(-1);
        this.onNextClick = () => this.scrollByCard(1);
        this.onTrackScroll = this.debounce(() => this.updateArrowState(), 80);
        this.onResize = this.debounce(() => this.updateArrowState(), 120);
        this.onVisibilityChange = () => {
          if (document.hidden) this.videos.forEach((video) => video.pause());
        };

        this.previousButton?.addEventListener('click', this.onPreviousClick);
        this.nextButton?.addEventListener('click', this.onNextClick);
        this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        this.setupVariantSelectors();
        this.setupVideoControls();
        this.setupAutoplay();
        this.updateArrowState();
      }

      disconnectedCallback() {
        this.previousButton?.removeEventListener('click', this.onPreviousClick);
        this.nextButton?.removeEventListener('click', this.onNextClick);
        this.track?.removeEventListener('scroll', this.onTrackScroll);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.videoObserver?.disconnect();
      }

      setupVariantSelectors() {
        this.querySelectorAll('[data-vpc-variant-select]').forEach((select) => {
          const updateVariant = () => {
            const option = select.selectedOptions[0];
            const card = select.closest('[data-vpc-card]');
            if (!option || !card) return;

            const price = card.querySelector('[data-vpc-price]');
            const comparePrice = card.querySelector('[data-vpc-compare-price]');
            const addButton = card.querySelector('[data-vpc-add-button]');
            const available = option.dataset.available === 'true';

            if (price) price.textContent = option.dataset.price || '';

            if (comparePrice) {
              const compareValue = option.dataset.comparePrice || '';
              comparePrice.textContent = compareValue;
              comparePrice.hidden = compareValue === '';
            }

            if (addButton) {
              addButton.disabled = !available;
              addButton.setAttribute('aria-disabled', available ? 'false' : 'true');
              const label = addButton.querySelector('[data-vpc-button-label]');
              if (label) label.textContent = available ? addButton.dataset.addLabel : addButton.dataset.soldOutLabel;
            }
          };

          select.addEventListener('change', updateVariant);
          updateVariant();
        });
      }

      setupVideoControls() {
        this.videos.forEach((video) => {
          video.muted = true;
          video.playsInline = true;

          video.addEventListener('click', () => {
            if (video.paused) {
              this.videos.forEach((otherVideo) => {
                if (otherVideo !== video) otherVideo.pause();
              });
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });

          const card = video.closest('[data-vpc-card]');
          const soundButton = card?.querySelector('[data-vpc-sound]');
          if (!soundButton) return;

          soundButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (video.muted) {
              this.videos.forEach((otherVideo) => {
                if (otherVideo !== video) {
                  otherVideo.muted = true;
                  const otherButton = otherVideo.closest('[data-vpc-card]')?.querySelector('[data-vpc-sound]');
                  if (otherButton) {
                    otherButton.dataset.muted = 'true';
                    otherButton.setAttribute('aria-label', otherButton.dataset.unmuteLabel);
                  }
                }
              });
            }

            video.muted = !video.muted;
            soundButton.dataset.muted = String(video.muted);
            soundButton.setAttribute('aria-label', video.muted ? soundButton.dataset.unmuteLabel : soundButton.dataset.muteLabel);

            if (video.paused) video.play().catch(() => {});
          });
        });
      }

      setupAutoplay() {
        if (!this.autoplayEnabled || this.reducedMotion || this.videos.length === 0) return;

        if (!('IntersectionObserver' in window)) {
          this.videos[0]?.play().catch(() => {});
          return;
        }

        this.videoVisibility = new Map(this.videos.map((video) => [video, 0]));
        this.videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.videoVisibility.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
            });

            let activeVideo = null;
            let highestRatio = 0;
            this.videoVisibility.forEach((ratio, video) => {
              if (ratio >= 0.6 && ratio > highestRatio) {
                activeVideo = video;
                highestRatio = ratio;
              }
            });

            this.videos.forEach((video) => {
              if (video === activeVideo && !document.hidden) {
                video.play().catch(() => {});
              } else {
                video.pause();
              }
            });
          },
          { threshold: [0, 0.25, 0.6, 0.85, 1] }
        );

        this.videos.forEach((video) => this.videoObserver.observe(video));
      }

      scrollByCard(direction) {
        const firstCard = this.track.querySelector('[data-vpc-card]');
        if (!firstCard) return;

        const styles = window.getComputedStyle(this.track);
        const gap = parseFloat(styles.columnGap || styles.gap || 0);
        const distance = firstCard.getBoundingClientRect().width + gap;
        this.track.scrollBy({ left: distance * direction, behavior: 'smooth' });
      }

      updateArrowState() {
        if (!this.track) return;
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const tolerance = 3;
        const hasOverflow = maxScroll > tolerance;

        if (this.arrows) this.arrows.hidden = !hasOverflow;
        if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= tolerance;
        if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - tolerance;
      }

      debounce(callback, delay) {
        let timeout;
        return (...args) => {
          window.clearTimeout(timeout);
          timeout = window.setTimeout(() => callback(...args), delay);
        };
      }
    }
  );
}
